/**
 * Smoothly scrolls to a specified element or selector.
 *
 * @param {HTMLElement|string} elementOrSelector - The target element or CSS selector to scroll to.
 * @param {Object} [options={}] - Optional settings for scrolling behavior.
 * @param {number} [options.offset=0] - Additional offset to adjust the scroll position. (positive = push farther down)
 * @param {ScrollBehavior} [options.easing='smooth'] - Scrolling behavior: 'smooth' or 'auto'.
 * @param {number} [options.headerOffset] - Extra pixels to subtract for sticky headers. If provided and native scroll is used, it sets scroll-padding-top.
 * @param {HTMLElement|Window} [options.container=window] - Scrollable container; default is window.
 * @param {boolean} [options.preferNative=true] - Use element.scrollIntoView when possible (honors scroll-margin-top).
 * @returns {void}
 */

import { logger } from 'data-layer/exports';

export const scrollTo = (
  elementOrSelector: HTMLElement | string,
  options: {
    offset?: number;
    easing?: ScrollBehavior;
    headerOffset?: number;
    container?: HTMLElement | Window;
    preferNative?: boolean;
  } = {},
): void => {
  const resolveElement = (target: HTMLElement | string): HTMLElement | null => {
    if (target instanceof HTMLElement) return target;
    if (typeof target !== 'string') return null;
    const sel = target.trim();
    // If it's an ID hash, avoid invalid selector errors like '#1'
    if (sel.startsWith('#')) {
      const raw = decodeURIComponent(sel.slice(1));
      const byId = document.getElementById(raw);
      if (byId) return byId;
      // Fallbacks that won't throw on weird IDs
      if (
        (window).CSS &&
        typeof (window).CSS.escape === 'function'
      ) {
        const esc = (window).CSS.escape(raw);
        return (
          document.querySelector<HTMLElement>(`#${esc}`) ||
          document.querySelector<HTMLElement>(`[name="${esc}"]`)
        );
      }
      const safeAttr = raw.replace(/"/g, '\\"');
      return (
        document.querySelector<HTMLElement>(`[id="${safeAttr}"]`) ||
        document.querySelector<HTMLElement>(`[name="${safeAttr}"]`)
      );
    }
    try {
      return document.querySelector<HTMLElement>(sel);
    } catch {
      return null;
    }
  };

  const element = resolveElement(elementOrSelector);

  if (!element) {
    logger.warn(
      'scrollTo: Element not found',
      { selector: elementOrSelector },
      'infrastructure',
    );
    return;
  }

  if (!(element instanceof HTMLElement)) {
    logger.warn(
      'scrollTo: Target is not a valid HTMLElement',
      { element: typeof element },
      'infrastructure',
    );
    return;
  }

  const defaultOptions = {
    offset: 0,
    easing: 'smooth' as ScrollBehavior,
    headerOffset: undefined as number | undefined,
    container: window as HTMLElement | Window,
    preferNative: true,
  };

  const finalOptions = { ...defaultOptions, ...options };

  // Respect prefers-reduced-motion
  const prefersReduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const behavior = prefersReduced ? 'auto' : finalOptions.easing;

  // Preferred: native path that honors scroll-margin-top on the target
  if (
    finalOptions.preferNative &&
    typeof element.scrollIntoView === 'function'
  ) {
    if (typeof finalOptions.headerOffset === 'number') {
      // Help native anchor behavior by setting scroll-padding-top
      const pad = Math.max(
        0,
        finalOptions.headerOffset + (finalOptions.offset || 0),
      );
      document.documentElement.style.scrollPaddingTop = `${pad}px`;
    }
    try {
      element.scrollIntoView({
        behavior,
        block: 'start',
        inline: 'nearest',
      });
      return;
    } catch {
      // fall through to manual
    }
  }

  // Manual path: compute top with header subtraction
  const container = finalOptions.container;
  const header =
    typeof finalOptions.headerOffset === 'number'
      ? finalOptions.headerOffset
      : 0;

  const rectTop = element.getBoundingClientRect().top;
  const currentScroll =
    container === window
      ? window.scrollY
      : (container as HTMLElement).scrollTop;

  const targetTop =
    currentScroll + rectTop - header + (finalOptions.offset || 0);

  if (container === window) {
    window.scrollTo({ top: targetTop, left: 0, behavior });
  } else {
    (container as HTMLElement).scrollTo({ top: targetTop, left: 0, behavior });
  }
};
